import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  changeMemberRole,
  getMembers,
  inviteMember,
  removeMember,
  renameTenant,
  resendInvitation,
  revokeInvitation,
  type Invitation,
  type Member,
} from './api';
import type { Translate } from './i18n';
import { tenantLabel } from './tenant';
import { ActionRow, Field, FieldGrid, Section } from './ui';

interface TeamMembership {
  tenantId: string;
  tenantName: string;
  tenantStatus: string;
  role: string;
}

/** Tenant-level administration. It deliberately does not depend on a map. */
export function TeamPage({
  memberships,
  t,
  onClose,
  onChanged,
}: {
  memberships: TeamMembership[];
  t: Translate;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const teams = useMemo(
    () => memberships.filter((m) => m.role === 'tenant_admin' && m.tenantStatus === 'active'),
    [memberships],
  );
  const [tenantId, setTenantId] = useState(teams[0]?.tenantId ?? '');
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [teamName, setTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('contributor');
  const [inviteLink, setInviteLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const team = teams.find((candidate) => candidate.tenantId === tenantId) ?? teams[0] ?? null;

  useEffect(() => {
    if (!team && teams[0]) setTenantId(teams[0].tenantId);
  }, [team, teams]);

  useEffect(() => {
    if (!team) {
      setMembers([]);
      setInvitations([]);
      setTeamName('');
      return;
    }
    setTeamName(team.tenantName);
    setInviteLink('');
    void (async () => {
      try {
        const result = await getMembers(team.tenantId);
        setMembers(result.members);
        setInvitations(result.invitations);
      } catch (err) {
        setMessage(String(err));
      }
    })();
  }, [team]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setMessage('');
    try {
      await action();
      await onChanged();
    } catch (err) {
      setMessage(String(err));
    } finally {
      setBusy(false);
    }
  };

  const reloadMembers = async (id: string) => {
    const result = await getMembers(id);
    setMembers(result.members);
    setInvitations(result.invitations);
  };

  const onRename = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!team || !teamName.trim() || teamName.trim() === team.tenantName) return;
    await run(async () => {
      const result = await renameTenant(team.tenantId, teamName.trim());
      setTeamName(result.name);
      setMessage(t('team.renamed'));
    });
  };

  const onInvite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!team || !inviteEmail.trim()) return;
    await run(async () => {
      const result = await inviteMember(team.tenantId, inviteEmail.trim(), inviteRole);
      setInviteLink(result.link);
      setInviteEmail('');
      setMessage(
        result.emailQueued ? t('settings.members.invitedEmail') : t('settings.members.invitedManual'),
      );
      await reloadMembers(team.tenantId);
    });
  };

  const onResend = async (invitation: Invitation) => {
    if (!team) return;
    await run(async () => {
      const result = await resendInvitation(team.tenantId, invitation.id);
      setInviteLink(result.link);
      setMessage(
        result.emailQueued ? t('settings.members.resentEmail') : t('settings.members.resentManual'),
      );
      await reloadMembers(team.tenantId);
    });
  };

  const onRevoke = async (invitation: Invitation) => {
    if (!team) return;
    await run(async () => {
      await revokeInvitation(team.tenantId, invitation.id);
      await reloadMembers(team.tenantId);
    });
  };

  const onChangeRole = async (member: Member, nextRole: string) => {
    if (!team || nextRole === member.role) return;
    await run(async () => {
      await changeMemberRole(team.tenantId, member.userId, nextRole);
      setMessage(t('settings.members.roleChanged'));
      await reloadMembers(team.tenantId);
    });
  };

  const onRemove = async (member: Member) => {
    if (!team) return;
    if (!window.confirm(t('settings.members.removeConfirm', { email: member.email }))) return;
    await run(async () => {
      await removeMember(team.tenantId, member.userId);
      setMessage(t('settings.members.removed'));
      await reloadMembers(team.tenantId);
    });
  };

  return (
    <div className="admin-page">
      <div className="admin-head">
        <div>
          <h1>{t('team.title')}</h1>
          <p className="muted">{t('team.hint')}</p>
        </div>
        <button className="button" type="button" onClick={onClose}>
          {t('team.back')}
        </button>
      </div>

      {message && <div className="warning attention">{message}</div>}

      {teams.length === 0 && <p className="muted">{t('admin.noTenants')}</p>}

      {teams.length > 1 && (
        <Section title={t('team.choose')}>
          <Field label={t('team.choose')}>
            <select value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
              {teams.map((candidate) => (
                <option key={candidate.tenantId} value={candidate.tenantId}>
                  {tenantLabel(candidate.tenantName, candidate.tenantId)}
                </option>
              ))}
            </select>
          </Field>
        </Section>
      )}

      {team && (
        <>
          <Section title={tenantLabel(team.tenantName, team.tenantId)}>
            <form className="ui-form" onSubmit={onRename}>
              <Field label={t('team.name')}>
                <input value={teamName} onChange={(event) => setTeamName(event.target.value)} />
              </Field>
              <ActionRow>
                <button
                  type="submit"
                  className="button"
                  disabled={busy || !teamName.trim() || teamName.trim() === team.tenantName}
                >
                  {t('team.rename')}
                </button>
              </ActionRow>
            </form>
          </Section>

          <Section title={t('settings.members')} description={t('settings.members.hint')}>
            <form className="ui-form" onSubmit={onInvite}>
              <FieldGrid>
                <Field label={t('settings.members.email')}>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(event) => setInviteEmail(event.target.value)}
                  />
                </Field>
                <Field label={t('settings.members.role')}>
                  <select value={inviteRole} onChange={(event) => setInviteRole(event.target.value)}>
                    <option value="contributor">{t('role.contributor')}</option>
                    <option value="viewer">{t('role.viewer')}</option>
                    <option value="tenant_admin">{t('role.tenant_admin')}</option>
                  </select>
                </Field>
              </FieldGrid>
              <ActionRow>
                <button
                  type="submit"
                  className="button"
                  disabled={busy || !inviteEmail.trim()}
                >
                  {t('settings.members.invite')}
                </button>
              </ActionRow>
            </form>
            {inviteLink && (
              <Field label={t('settings.members.link')}>
                <input readOnly value={inviteLink} onFocus={(event) => event.currentTarget.select()} />
              </Field>
            )}
          </Section>

          <Section title={t('settings.members.pending')}>
            {invitations.length === 0 && <p className="muted">{t('settings.members.noPending')}</p>}
            <ul className="member-list">
              {invitations.map((invitation) => (
                <li key={invitation.id}>
                  <span className="change-name">{invitation.email}</span>
                  <span className="chip">{t(`role.${invitation.role}`)}</span>
                  <em>
                    {t('settings.members.expires')}:{' '}
                    {new Date(invitation.expiresAt).toLocaleDateString()}
                  </em>
                  <button
                    type="button"
                    className="button"
                    disabled={busy}
                    onClick={() => void onResend(invitation)}
                  >
                    {t('settings.members.resend')}
                  </button>
                  <button
                    type="button"
                    className="button danger"
                    disabled={busy}
                    onClick={() => void onRevoke(invitation)}
                  >
                    {t('settings.members.revoke')}
                  </button>
                </li>
              ))}
            </ul>
          </Section>

          <Section title={t('settings.members.current')}>
            {members.length === 0 && <p className="muted">{t('settings.members.none')}</p>}
            <ul className="member-list">
              {members.map((member) => {
                const lastAdmin =
                  member.role === 'tenant_admin' &&
                  members.filter((candidate) => candidate.role === 'tenant_admin').length <= 1;
                return (
                  <li key={member.userId}>
                    <span className="change-name">
                      {member.name ? `${member.name} · ${member.email}` : member.email}
                    </span>
                    {member.deactivatedAt && (
                      <span className="badge aborted">{t('admin.inactive')}</span>
                    )}
                    <select
                      value={member.role}
                      disabled={busy || lastAdmin}
                      onChange={(event) => void onChangeRole(member, event.target.value)}
                    >
                      <option value="tenant_admin">{t('role.tenant_admin')}</option>
                      <option value="contributor">{t('role.contributor')}</option>
                      <option value="viewer">{t('role.viewer')}</option>
                    </select>
                    <em>
                      {t('settings.members.lastLogin')}:{' '}
                      {member.lastLoginAt
                        ? new Date(member.lastLoginAt).toLocaleDateString()
                        : '—'}
                    </em>
                    <button
                      type="button"
                      className="button danger"
                      disabled={busy || lastAdmin}
                      onClick={() => void onRemove(member)}
                    >
                      {t('settings.members.remove')}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Section>
        </>
      )}
    </div>
  );
}
